import { DateProvider } from "@/shared/DateProvider";
import { MediaImageType } from "./media_image_type";
import { RepositoryToUrlMap } from "./repositoryToUrlMap";
import { VirtualFileSystem } from "./VirtualFileSystem";
import { FileSystemRecord } from "./FileSystemRecord";

export class ImageFileSystemRecord extends FileSystemRecord {
  
  get layerId(): string  {
    return this.addtionalMetadata.layerId as string
  }

  get stateId(): string  {
    return this.addtionalMetadata.stateId as string
  }

  get mediaType(): MediaImageType  {
    return this.addtionalMetadata.mediaType as MediaImageType
  }

  static from(record: FileSystemRecord): ImageFileSystemRecord{
    let mediaRecord = new ImageFileSystemRecord(record.blob, 
        record.modificationDate, 
        record.createdDate, 
        record.undoDate ?? record.modificationDate, 
        record.size, 
        record.systemFileNumber, 
        record.addtionalMetadata?.layerId, 
        record.addtionalMetadata?.stateId, 
        record.addtionalMetadata?.mediaType,
        record.format
      );
      return mediaRecord;
    }
  /**
   *
   */
  constructor(
    blob: Blob | null,
    modificationDate: Date,
    createdDate: Date,
    undoDate: Date,
    size: number,
    systemFileNumber: number,
    layerId: string,
    stateId: string,
    mediaType: MediaImageType,
    format: string
  ) {

    let metadata = {
      layerId: layerId,
      stateId: stateId,
      mediaType: mediaType
    }

    super(blob,
      modificationDate,
      createdDate, 
      undoDate,
      size,
      systemFileNumber,
      false,
      metadata);

    this.format = format
  }
}

export class ImageFilesystem {
    private defaultFormat = 'png';

  // thanks - https://www.typescriptlang.org/docs/handbook/utility-types.html
   // project_id | layer_id | state_id | asset_type | modified_date | created_date | blob_url (edited)
    private projectId: string;
/**
 *
 */
    public getProjectId(): string {
      return this.projectId;
    }

    constructor(projectId: string) {
      this.projectId = projectId;
    }

    update(newProjectId: string) {
      this.projectId = newProjectId;
    }

    async clone(targetUrl: string){
      let newFileSystem = await VirtualFileSystem.getInstance().link(this.getProjectId(), targetUrl)
      return newFileSystem;
    }

    private async getUrlWithExtension(layerId: string, imgType: MediaImageType, layerStateId: string){
      let url = RepositoryToUrlMap.constructUrl(this.projectId, layerId, imgType as MediaImageType, layerStateId)
      let extension = await VirtualFileSystem.getInstance().getFileExtensionForUrl(url) ?? this.defaultFormat;
      let urlWithExtension = RepositoryToUrlMap.constructUrl(this.projectId, layerId, imgType as MediaImageType, layerStateId, extension)
      return urlWithExtension
    }

    async duplicate(originalLayer: string, newLayer: string, layerStateId: string) {
      const urls = await Promise.all(
        Object.values(MediaImageType).map(async value => {
          const src = await this.getUrlWithExtension(originalLayer, value as MediaImageType, layerStateId);
          const target = await this.getUrlWithExtension(newLayer, value as MediaImageType, layerStateId);
          return { src, target };
        })
      );

      await Promise.all(
        urls.map(({ src, target }) =>
          VirtualFileSystem.getInstance().copy(src, target)
        )
      );
    }

    async removeImage(layerId: string, layerStateId: string, imgType: MediaImageType) {
        let url = await this.getUrlWithExtension(layerId, imgType as MediaImageType, layerStateId)
        VirtualFileSystem.getInstance().delete(url)
    }

    async getRecord(layerId: string, layerStateId: string, imgType: MediaImageType): Promise<FileSystemRecord | null> {
      let url = await this.getUrlWithExtension(layerId, imgType as MediaImageType, layerStateId)
      let record = await VirtualFileSystem.getInstance().get(url)
      return Promise.resolve(record);
    }

    async restoreUndoDate(layerId: string, layerStateId: string, imgType: MediaImageType): Promise<FileSystemRecord | null> {
      let url = await this.getUrlWithExtension(layerId, imgType as MediaImageType, layerStateId)
      let record = VirtualFileSystem.getInstance().restoreUndoDate(url, DateProvider.getUTCDate())
      return Promise.resolve(record);
    }


    async getImage(layerId: string, layerStateId: string, imgType: MediaImageType): Promise<string | null> {
      let record = await this.getRecord(layerId, layerStateId, imgType)
      return record?.getBlobUrl();
    }

    async getImagePath(layerId: string, layerStateId: string, imgType: MediaImageType): Promise<string | null> {
      let url = this.getUrlWithExtension(layerId, imgType as MediaImageType, layerStateId)
      return url
    }

    async link(layerId: string, layerStateId: string, srcImageType: MediaImageType, targetImageType: MediaImageType): Promise<void> {
      let srcUrl = await this.getUrlWithExtension(layerId, srcImageType as MediaImageType, layerStateId)
      let targetUrl = await this.getUrlWithExtension(layerId, targetImageType as MediaImageType, layerStateId)
      await VirtualFileSystem.getInstance().link(srcUrl, targetUrl);
    }

    setImage(layerId: string, layerStateId: string, imgType: MediaImageType, image: string): Promise<boolean> {
      return new Promise(async (resolve) => {
        try {
          let url = await this.getUrlWithExtension(layerId, imgType as MediaImageType, layerStateId)
          let blob: Blob;
          if (typeof image === "string") {
            // data-URL or object-URL → Blob
            blob = await VirtualFileSystem.getInstance().blobUrlToBlob(image);
          } else {
            blob = image;
          }
          let size = blob.size / 1024;
          let systemFileNumber = VirtualFileSystem.getInstance().generateRandom12DigitNumber();
          let creationDate =  DateProvider.getUTCDate()
          let record = new ImageFileSystemRecord(blob,
            creationDate,
            creationDate,
            creationDate,
            size,
            systemFileNumber,
            layerId,
            layerStateId,
            imgType,
            this.defaultFormat
          )

          await VirtualFileSystem.getInstance().write(url, record)
          return resolve(true);
        }
        catch (error) {
          console.error("Error setting image:", error);
          resolve(false);
        }
      })
    }

    async clearByPrefix(prefix: string, idsToPreserve: string[] = []): Promise<void> {
        return VirtualFileSystem.getInstance().deleteByPrefix(prefix, idsToPreserve);
    }
}
